import React from 'react';
import PropTypes from 'prop-types';
import { createLabelHelper } from 'infrastructure/js/utils/labelHelper';
import { reduxForm } from 'redux-form';
import Dialog from 'infrastructure/js/components/Dialog/dialog';
import {filterTypes} from 'infrastructure/js/enums/filterTypes';
import Grid from 'infrastructure/js/components/Grid/gridWrapper';
import {gridsNames} from '../../../../enums/gridsNames.js';
import LabelCell from '../../Common/Cells/LabelCell/labelCell.js';
import Label from 'infrastructure/js/components/Label/label';
import MultiRowsCell from 'infrastructure/js/components/Grid/MulitRowsCell/multiRowsCell';
import editableViewCell from '../../Common/Cells/InputCell/editableViewCell';
import PermissionManager from 'infrastructure/js/utils/permissionManager';
import { focusElementWhenReady } from 'infrastructure/js/utils/domHelper';
import { validateBarcodes } from './barcodeValidation';

require('./SelectNestsStep.scss');

export class SelectNestsSecondStep extends React.PureComponent {

  constructor(props) {
    super(props);

    // Sub-row keys ("nestBusinessId__index") the last Validate click found invalid on the
    // Barcode column - see validateBarcodes(). Local-only: nothing outside this grid needs it.
    this.state = {barcodeErrors: new Set()};

    this.gridLabels = createLabelHelper('mat.wizards.import.files.reportedCutsAndKits.grid.selectesnests.');
    this.labels = createLabelHelper('mat.wizards.import.files.importCutPlanWithRoll.');
    this.showBarcodeColumn = PermissionManager.getOrgPreferences().sapVerificationEnabled;
    this.showTagColumn = PermissionManager.getOrgPreferences().quickAssignTagEnabled;
    this.showScrapColumn = PermissionManager.hasScrapViewPermissions();
    this.columnsConfig = this.createColumnsConfig();
  }


  componentDidMount() {
    this.setIsRowDisabled();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedNests !== this.props.selectedNests) {
      this.setIsRowDisabled();
    }
  }

  isRowEditable = (params) => {
    return !params.data.isRowDisabled;
  };

  isPlyEditable = (params) => {
    return this.isRowEditable(params) && !(params.data.nestWithRollFromFile?.every(item => item.isKitTypeHasPlies) ?? false);
  };

  createColumnsConfig = () => {

    let columnsConfig =  [
      {
        fieldName: 'nestBusinessId',
        title: this.gridLabels.get('columns.nest.title'),
        filterType: filterTypes.NONE,
        columnOptions: {
          sortable: false,
          cellComponent: LabelCell,
          valueGetter: (params) => {
            return {errorMessage: params.data.errorPlan ,value: params.data.nestBusinessId, showOnlyError: false};
          },
        }
      },
      {
        fieldName: 'kitTypeBusinessId',
        title: this.gridLabels.get('columns.kittype.title'),
        filterType: filterTypes.NONE,
        columnOptions: {
          cellComponent: MultiRowsCell,
          valueGetter: (params) => {
            return this.multiValueGetter(params, 'kitTypeBusinessId', ['kitTypeBusinessIdError'], true);
          },
          sortable: false,
        }
      },
      {
        fieldName: 'kitBusinessId',
        title: this.gridLabels.get('columns.kitid.title'),
        filterType: filterTypes.NONE,
        columnOptions: {
          cellComponent: MultiRowsCell,
          valueGetter: (params) => {
            return this.multiValueGetter(params, 'kitBusinessId', ['lengthCutStringError', 'tagKeyError'], false);
          },
          sortable: false,
        }
      },
      {
        fieldName: 'woBusinessId',
        title: this.gridLabels.get('columns.wo.title'),
        filterType: filterTypes.NONE,
        columnOptions: {
          cellComponent: MultiRowsCell,
          valueGetter: (params) => {
            return this.multiValueGetter(params, 'woBusinessId', ['woBusinessIdError'], true);
          },
          sortable: false,
        }
      },
      {
        fieldName: 'quantityPlies',
        title: this.gridLabels.get('columns.expectedplies.title'),
        filterType: filterTypes.NONE,
        columnOptions: {
          cellComponent: MultiRowsCell,
          valueGetter: (params) => {
            return this.multiValueGetter(params, 'quantityPlies');
          },
          sortable: false,
        }
      },
      {
        fieldName: 'quantityKittedPlies',
        title: this.gridLabels.get('columns.actualplies.title'),
        filterType: filterTypes.NONE,
        columnOptions: {
          cellComponent: MultiRowsCell,
          valueGetter: (params) => {
            return this.editableValueGetter(params, 'quantityKittedPlies', true);
          },
          editable: this.isPlyEditable,
          cellEditor: 'editableMultiCell',
          cellEditorParams: {
            inputType: 'numeric',
            maxLength: 9999,
            isInt: true,
            shouldStopEditingOnBlur: false
          },
          onCellValueChanged: (params) => {
            params.data.nestWithRollFromFile.map((nestWithRoll, index) => {
              nestWithRoll.quantityKittedPlies = params.newValue.dataList[index].value;
            })
            this.onCellEdited();
          },

          sortable: false,
        }
      },
      {
        fieldName: 'materialUsedAmount',
        title: this.gridLabels.get('columns.materialUsedAmount.title'),
        filterType: filterTypes.NONE,
        columnOptions: {
          cellComponent: MultiRowsCell,
          valueGetter: (params) => {
            return this.editableValueGetter(params, 'materialUsedAmount');
          },
          editable: this.isRowEditable,
          cellEditor: 'editableMultiCell',
          cellEditorParams: {
            inputType: 'numeric',
            maxLength: 9999,
            shouldStopEditingOnBlur: false
          },
          onCellValueChanged: (params) => {
            params.data.nestWithRollFromFile.map((nestWithRoll, index) => {
              nestWithRoll.materialUsedAmount = params.newValue.dataList[index].value;
            })
            this.onCellEdited();
          },
          sortable: false,
        }
      },
      ...(this.showBarcodeColumn ? [
      {
        fieldName: 'sapBarcode',
        title: this.gridLabels.get('columns.barcode.title'),
        filterType: filterTypes.NONE,
        columnOptions: {
          cellComponent: MultiRowsCell,
          valueGetter: (params) => {
            return this.editableValueGetter(params, 'sapBarcode', false, this.state.barcodeErrors);
          },
          editable: this.isRowEditable,
          cellEditor: 'editableMultiCell',
          cellEditorParams: {
            inputType: 'string',
            shouldStopEditingOnBlur: false,
            onEnterPressed: this.handleScanEnter,
          },
          suppressKeyboardEvent: this.suppressEnterKeyboardEvent,
          onCellValueChanged: (params) => {
            params.data.nestWithRollFromFile.map((nestWithRoll, index) => {
              nestWithRoll.sapBarcode = params.newValue.dataList[index].value;
            })
            this.onCellEdited();
          },

          sortable: false,
        }
      }] : []),
      ...(this.showTagColumn ? [
        {
          fieldName: 'tagKey',
          title: this.gridLabels.get('columns.tag.title'),
          filterType: filterTypes.NONE,
          columnOptions: {
            cellComponent: MultiRowsCell,
            valueGetter: (params) => {
              return this.editableValueGetter(params, 'tagKey');
            },
            editable: this.isRowEditable,
            cellEditor: 'editableMultiCell',
            cellEditorParams: {
              inputType: 'string',
              shouldStopEditingOnBlur: false,
              onEnterPressed: this.handleScanEnter,
            },
            suppressKeyboardEvent: this.suppressEnterKeyboardEvent,
            onCellValueChanged: (params) => {
              params.data.nestWithRollFromFile.map((nestWithRoll, index) => {
                nestWithRoll.tagKey = params.newValue.dataList[index].value;
              })
              this.onCellEdited();
            },

            sortable: false,
          }
        }] : []),

      ...(this.showScrapColumn ? [{
          fieldName: 'materialScrapAmount',
          title: this.gridLabels.get('columns.materialScrapAmount.title'),
          filterType: filterTypes.NONE,
          columnOptions: {
            cellComponent: MultiRowsCell,
            valueGetter: (params) => {
              return this.editableValueGetter(params, 'materialScrapAmount');
            },
            editable: this.isRowEditable,
            cellEditor: 'editableMultiCell',
            cellEditorParams: {
              inputType: 'numeric',
              maxLength: 9999,
              shouldStopEditingOnBlur: false
            },
            onCellValueChanged: (params) => {
              params.data.nestWithRollFromFile.map((nestWithRoll, index) => {
                nestWithRoll.materialScrapAmount = params.newValue.dataList[index].value;
              })
              this.onCellEdited();
            },
            sortable: false,
          }
        }] : [])
    ];

    return columnsConfig;
  };

  goOn = () => {
    // Commit whatever cell is still in edit mode before reading the grid.
    //
    // The Barcode/Tag editors deliberately run with shouldStopEditingOnBlur: false, because the
    // scanner's Enter-advance drives editing from cell to cell itself and must not be cut short
    // by the blur that move causes. The side effect is that clicking Validate while an input
    // still holds focus leaves the editor open, so ag-grid never writes the typed value back
    // into the row - validation and the SAP request would both read the value as it was BEFORE
    // the last scan. stopEditing() closes that window; it commits, it does not focus anything,
    // so the scan sequence is untouched.
    this.gridRef?.api?.stopEditing();

    const selectedNests = this.props.nestsItems.filter((nest) => {
      let isSelected = this.props.selectedNestsStepTwo.findIndex((selectedNest) => selectedNest.nestBusinessId === nest.nestBusinessId) !== -1;
      nest.selected = isSelected;
      return isSelected;
    });

    let barcodeErrors = this.showBarcodeColumn ? validateBarcodes(selectedNests) : new Set();

    // Unconditional: this both paints newly found errors and clears highlighting left over from
    // a previous attempt. force: true re-runs the value getters, which is what re-reads
    // this.state.barcodeErrors - without it the red only appears once something else happens to
    // redraw the grid.
    this.setState({barcodeErrors}, () => {
      this.gridRef?.api?.refreshCells({force: true});
    });

    if (barcodeErrors.size > 0) {
      return;
    }

    this.props.actions.validateFile(this.props.sSelectedFile, selectedNests, this.props.sIgnoreSapVerification)
  };

  // Any committed edit makes the previous verdict stale, so all of it is dropped: the error
  // banner under the grid (which is also what keeps the Validate button disabled - see
  // getDialogButtons), the red Barcode highlighting, and - via the reducer - the SAP
  // verification failure along with any "continue without" the operator had ticked for it.
  //
  // The whole barcode error set goes, not just the edited cell's entry: uniqueness is judged
  // across the entire grid, so fixing one duplicate necessarily clears its twin elsewhere.
  onCellEdited = () => {
    if (this.state.barcodeErrors.size > 0) {
      this.setState({barcodeErrors: new Set()});
    }

    if (this.props.sNestsErrors) {
      this.props.actions.onRowsChanged(this.props.nestsItems);
    }
  };

  // Grid passes its own component instance (not the bare api) to setRef, hence gridRef.api.
  // NOTE: this has to travel inside gridProps - gridWrapper only forwards gridProps and its
  // grid actions, so a top-level setRef prop is silently dropped.
  setGridRef = (gridRef) => {
    this.gridRef = gridRef;
  };

  goBack = () => {
    let wizardActions = this.props.wizardActions;
    wizardActions.previous();
  };

  onHide = () => {
    this.props.actions.onWizardClose();
    let wizardActions = this.props.wizardActions;
    wizardActions.close();
  };

  // A SAP verification failure is the one error the operator is allowed to overrule, so it is
  // the one error whose banner does not permanently disable the button - ticking the footer
  // checkbox re-enables it. Every other nestsErrors value (CSV parse, roll length, inspection
  // status) still blocks outright, and the local barcode errors block inside goOn regardless.
  isSubmitBlockedByErrors() {
    if (!this.props.sNestsErrors) {
      return false;
    }

    return !(this.props.sSapVerificationFailed && this.props.sIgnoreSapVerification);
  }

  getDialogButtons() {
    let buttonOK = {
      id:'submit',
      text: this.labels.get('footer.selectnestsok'),
      bsStyle: 'primary',
      disabled: (this.props.sSelectedFile && !this.isSubmitBlockedByErrors() && this.props.selectedNestsStepTwo.size > 0) ? this.props.loading : true,
      loading: this.props.loading,
      action: this.props.handleSubmit(this.goOn)
    };

    let buttonCancel = {
      id:'cancel',
      text: this.labels.get('footer.back'),
      action: this.goBack
    };

    return { left: [buttonCancel], right: [buttonOK] };
  }

  // Mirrors the Move Assets dialog's footer override, minus its permission gate - GKN decided
  // any operator who can run the import may also accept it unverified.
  getFooterValidationCheckBox = () => {
    if (!this.props.sSapVerificationFailed) {
      return null;
    }

    return {
      label: this.labels.get('validation.sap.verification.ignore'),
      onChange: this.onFooterValidationChange,
    };
  };

  onFooterValidationChange = (value) => {
    this.props.actions.toggleIgnoreSapVerification(value);
  };

  // Shared shape for the "multi-row" editable columns: one enabled/value/change entry per
  // ply. Only the actual-plies column additionally disables plies that already have a kit.
  // errorKeys is only ever passed for the Barcode column (see createColumnsConfig) - other
  // editable columns keep getting hasError: false, i.e. no visual change for them.
  editableValueGetter = (entity, fieldName, checkKitTypeHasPlies = false, errorKeys = null) => {
    return {
      dataList: entity.data?.[fieldName]?.map((item, index) => {
        const isKitTypeHasPlies = checkKitTypeHasPlies && (entity.data.nestWithRollFromFile[index]?.isKitTypeHasPlies ?? false);
        return {
          enabled: !entity.data.isRowDisabled && !isKitTypeHasPlies,
          value: item,
          change: this.props.change,
          hasError: errorKeys ? errorKeys.has(`${entity.data.nestBusinessId}__${index}`) : false,
        }
      }) || [],
      componentType: editableViewCell
    };
  };

  // AG Grid's own keyboard handling intercepts Enter on an editing cell (to stop editing /
  // navigate) before it reaches the cell editor's onKeyDown - suppressing it here lets
  // EditableMultiCell's onEnterPressed (handleScanEnter) see and handle the key itself.
  suppressEnterKeyboardEvent = (params) => {
    return params.editing && params.event.key === 'Enter';
  };

  // Ordered list of the scan-target columns that are actually visible, so scanning
  // still works (advancing within the same column) when only one of Barcode/Tag is enabled.
  getScanColumns = () => {
    let columns = [];
    if (this.showBarcodeColumn) {
      columns.push('sapBarcode');
    }
    if (this.showTagColumn) {
      columns.push('tagKey');
    }
    return columns;
  };

  // For a given grid row, the ordered [Barcode(0), Tag(0), Barcode(1), Tag(1), ...] scan
  // targets, skipping sub-rows that are disabled (whole row disabled, or that ply has no kit).
  buildRowScanSequence = (rowIndex) => {
    let row = this.props.selectedNests.get(rowIndex);
    if (!row?.nestWithRollFromFile) {
      return [];
    }

    let columns = this.getScanColumns();
    let sequence = [];
    row.nestWithRollFromFile.forEach((nestWithRoll, subRowIndex) => {
      let isScannable = !row.isRowDisabled && !nestWithRoll.isKitTypeHasPlies;
      if (!isScannable) {
        return;
      }
      columns.forEach((colId) => sequence.push({colId, subRowIndex}));
    });
    return sequence;
  };

  findNextRowWithScanTargets = (startRowIndex) => {
    for (let rowIndex = startRowIndex; rowIndex < this.props.selectedNests.size; rowIndex++) {
      let sequence = this.buildRowScanSequence(rowIndex);
      if (sequence.length > 0) {
        return {rowIndex, sequence};
      }
    }
    return null;
  };

  focusScanTarget = (api, rowIndex, colId, subRowIndex) => {
    api.startEditingCell({rowIndex, colKey: colId});
    focusElementWhenReady(`${colId}${rowIndex}index${subRowIndex}`);
  };

  // Fired from the Barcode/Tag cell editors on Enter (i.e. a scanner completing a scan).
  // Moves focus to the next target in the Barcode<->Tag scan sequence, rolling over to
  // the next row once the current row's sub-rows are exhausted.
  handleScanEnter = ({api, rowIndex, colId, subRowIndex}) => {
    let sequence = this.buildRowScanSequence(rowIndex);
    let currentIndex = sequence.findIndex((target) => target.colId === colId && target.subRowIndex === subRowIndex);
    if (currentIndex === -1) {
      api.stopEditing();
      return;
    }

    let next = sequence[currentIndex + 1];
    if (next) {
      this.focusScanTarget(api, rowIndex, next.colId, next.subRowIndex);
      return;
    }

    let nextRow = this.findNextRowWithScanTargets(rowIndex + 1);
    if (!nextRow) {
      api.stopEditing();
      return;
    }

    this.focusScanTarget(api, nextRow.rowIndex, nextRow.sequence[0].colId, nextRow.sequence[0].subRowIndex);
  };

  multiValueGetter = (entity, field, errorFields = [], showOnlyError = false) => {
    return {
      dataList: entity.data.nestWithRollFromFile.map((item)=> {
        return {
          value: item[field],
          errorMessage: errorFields.map((errorField) => item[errorField]).filter(Boolean).join(', '),
          showOnlyError,
        }
      }),
      componentType: LabelCell
    };
  }

  renderErrors = () => {
    if(this.props.sNestsErrors){
       return this.renderErrorSelection(this.props.sNestsErrors);
    }
    return null;
  }

  renderErrorSelection = (data) => {
     let icon = 'pl pl-warning-icon';
     let errorType = data.statusType;
      return (
        <div className="error-selection">
          <div className='error-item'>
              <span className={`error-icon ${icon} ${errorType}`}></span>
              <label className='error-msg'>{data.message}</label>
          </div>
        </div>
      );
  };

  getRowHeight = (params) => {
    let rowHeight = 64;
    let rowCount = params.data.nestWithRollFromFile ? params.data.nestWithRollFromFile.length : 0;
    if (rowCount > 1) {
      rowHeight = (rowCount * 30) + 34;
    }

    return rowHeight;
  };

  setIsRowDisabled = () => {
    if(this.props.selectedNests) {
       this.props.selectedNests.map((obj) => {
        obj.isRowDisabled = obj.hasErrorProperties;
      });
    }
  };

  render() {
    let animation = this.props.isFirstLaunch;
    let plan = this.props.nestsItems?.[0]?.nestWithRollFromFile?.[0]?.plan;
    return (
      <Dialog
        id="select-nests-dialog"
        className="select-nests-dialog"
        animation={animation}
        show={true}
        onHide={this.onHide}
        titleText= {this.labels.get('selectnests.title')}
        moreInfoTextHeader={`${this.labels.get('filename.title')}: ${this.props.sSelectedFile ? this.props.sSelectedFile.name : ''}`}
        footerValidationCheckbox={this.getFooterValidationCheckBox()}
        footerButtons={this.getDialogButtons()}
        handleHashChange={false}
      >
        <div>
          <Label text={`${this.labels.get('plan.title')}: ${plan ? plan : ''}`} tooltip={plan ? plan : ''}/>
        </div>

        <div className="select-nests-grid">
          <Grid gridName={gridsNames.IMPORT_NESTS_WITH_ROLL_SELECT_NESTS_STEP_TWO}
                columnsConfig={this.columnsConfig}
                rows={this.props.selectedNests}
                isStatic={true}
                gridProps={
                  {
                    shouldPreSelectRows:true,
                    getRowHeight: this.getRowHeight,
                    sideBar: false,
                    setRef: this.setGridRef,
                  }
                }
          >

          </Grid>
        </div>

        {this.renderErrors()}

      </Dialog>
    );
  }
}

SelectNestsSecondStep.propTypes = {
  actions: PropTypes.object.isRequired,
  wizardActions: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  nestsItems: PropTypes.array.isRequired,
  selectedNests: PropTypes.object.isRequired,
  selectedNestsStepTwo: PropTypes.object.isRequired,
  sSelectedFile: PropTypes.object,
  sNestsErrors: PropTypes.object,
  sSapVerificationFailed: PropTypes.bool,
  sIgnoreSapVerification: PropTypes.bool,
  loading: PropTypes.bool,
  isFirstLaunch: PropTypes.bool,
};

SelectNestsSecondStep.defaultProps = {
  sSelectedFile: null,
  sNestsErrors: null,
  sSapVerificationFailed: false,
  sIgnoreSapVerification: false,
  loading: false,
  isFirstLaunch: false,
};

export default reduxForm({
    forceUnregisterOnUnmount: true,
    destroyOnUnmount: false
  }
)(SelectNestsSecondStep);

